import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PortfolioKeys } from "web_myplann/Api/Portfolio/portfolioKeys";
import {
	type ClientPortfolioDetailRelatedResponseDTO,
	type ClientRelatedListResponseDTO,
	deletePropertyIdRelatedRelatedIdV2,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";

type PropertyDetailWithRelated = {
	related: ClientPortfolioDetailRelatedResponseDTO[];
};

export const useUnpinPropertyRelatedMutation = (id: number) => {
	const queryClient = useQueryClient();
	const detailKey = PortfolioKeys.propertyDetail(id);

	return useMutation<
		ClientRelatedListResponseDTO,
		Error,
		number,
		{ previousDetail: PropertyDetailWithRelated | undefined }
	>({
		mutationFn: processResponse((relatedId: number) =>
			deletePropertyIdRelatedRelatedIdV2(id, relatedId),
		),
		onMutate: async (relatedId: number) => {
			await queryClient.cancelQueries({ queryKey: detailKey });

			const previousDetail =
				queryClient.getQueryData<PropertyDetailWithRelated>(detailKey);

			if (previousDetail) {
				queryClient.setQueryData<PropertyDetailWithRelated>(detailKey, {
					...previousDetail,
					related: previousDetail.related.filter(
						(item) => item.id !== relatedId,
					),
				});
			}

			return { previousDetail };
		},
		onError: (_error, _relatedId, context) => {
			if (context?.previousDetail) {
				queryClient.setQueryData(detailKey, context.previousDetail);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: PortfolioKeys.all });
		},
	});
};
